import React, { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { formatStringToDate } from "../../utils";
import { Loader2 } from "lucide-react";

interface Props {
  userProfile: any;
  topic: any;
  comments?: any;
  onLoadMore: () => void;
  hasNext: boolean;
  onLoading: boolean;
  isSent: boolean;
}

// Individual message bubble component
const MessageBubble = ({
  message,
  timestamp,
  isMe,
  isMyAgency,
  index,
}: {
  message: string;
  timestamp: string;
  isMe: boolean;
  isMyAgency: boolean;
  index: number;
}) => {
  const isOwnMessage = isMe || isMyAgency;

  return (
    <div
      className={cn(
        "flex flex-col mb-6",
        isOwnMessage ? "items-end" : "items-start",
        // Entrance animation
        "animate-in fade-in-0 slide-in-from-bottom-2",
        "duration-300 fill-mode-both"
      )}
      style={{
        animationDelay: `${index * 30}ms`,
      }}
    >
      {/* Sender label */}
      <span
        className={cn(
          "font-mono text-[10px] uppercase tracking-wider mb-1.5 px-1",
          isMe
            ? "text-teal-600 dark:text-teal-400"
            : isMyAgency
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-slate-500 dark:text-slate-400"
        )}
      >
        {isMe ? "You" : isMyAgency ? "Your Agency" : "External"}
      </span>

      {/* Message bubble */}
      <div
        className={cn(
          // Base styling
          "relative max-w-[85%] sm:max-w-[350px] px-4 py-3",
          "rounded-2xl",
          // Border
          "border",
          // Typography
          "font-sans text-[14px] leading-relaxed",
          // Position-based styling
          isOwnMessage ? "rounded-br-md" : "rounded-bl-md",
          // Color coding
          isMe
            ? [
                // User's messages - Teal
                "bg-teal-50 dark:bg-teal-950/40",
                "border-teal-200 dark:border-teal-800/50",
                "text-teal-900 dark:text-teal-100",
              ]
            : isMyAgency
            ? [
                // Agency messages - Emerald
                "bg-emerald-50 dark:bg-emerald-950/40",
                "border-emerald-200 dark:border-emerald-800/50",
                "text-emerald-900 dark:text-emerald-100",
              ]
            : [
                // External messages - Slate
                "bg-slate-100 dark:bg-slate-800/60",
                "border-slate-200 dark:border-slate-700/50",
                "text-slate-800 dark:text-slate-200",
              ],
          // Subtle shadow
          "shadow-sm"
        )}
      >
        {/* Message content */}
        <p className="whitespace-pre-wrap break-words">{message}</p>
      </div>

      {/* Timestamp */}
      <time
        className={cn(
          "font-mono text-[10px] mt-1.5 px-1",
          "text-slate-400 dark:text-slate-500",
          "tabular-nums"
        )}
      >
        {formatStringToDate(timestamp)}
      </time>
    </div>
  );
};

// Loading indicator
const LoadingIndicator = () => (
  <div className="flex items-center justify-center py-4">
    <Loader2 className="w-5 h-5 text-primary animate-spin" />
    <span className="ml-2 font-mono text-xs text-muted-foreground">
      Loading messages...
    </span>
  </div>
);

// Load more trigger
const LoadMoreTrigger = ({ onLoadMore }: { onLoadMore: () => void }) => (
  <button
    onClick={onLoadMore}
    className={cn(
      "w-full py-3 mb-4",
      "font-mono text-xs text-center",
      "text-primary hover:text-primary/80",
      "bg-slate-50/50 dark:bg-slate-800/30",
      "border border-dashed border-slate-200 dark:border-slate-700",
      "rounded-lg",
      "transition-colors duration-200",
      "hover:bg-slate-100/50 dark:hover:bg-slate-800/50"
    )}
  >
    Load older messages
  </button>
);

const MessageThread = ({
  userProfile,
  topic,
  comments,
  onLoadMore,
  hasNext,
  onLoading,
  isSent,
}: Props) => {
  const latestMessage = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom on new message
  useEffect(() => {
    if (isSent && latestMessage.current) {
      latestMessage.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [isSent, comments]);

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollHeight = target.scrollHeight;
    const scrollTop = Math.abs(target.scrollTop);
    const clientHeight = target.clientHeight;

    // Load more when scrolled near top (in reverse scroll)
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasNext && !onLoading) {
      onLoadMore();
    }
  };

  return (
    <ScrollArea
      className={cn(
        "h-[calc(100vh-400px)] min-h-[300px] max-h-[500px]",
        "pr-4 -mr-4"
      )}
    >
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          "flex flex-col-reverse",
          "px-1"
        )}
      >
        {/* Anchor for scrolling to latest message */}
        <div ref={latestMessage} className="h-0" />

        {/* Loading state */}
        {onLoading && comments?.length === 0 && <LoadingIndicator />}

        {/* Comments list - reversed display */}
        {comments?.map((comment: any, index: number) => (
          <MessageBubble
            key={comment.id}
            message={comment.message}
            timestamp={comment.createdAt}
            isMe={comment.userId === userProfile?.userId}
            isMyAgency={
              comment.companyId === userProfile?.companyId &&
              comment.userId !== userProfile?.userId
            }
            index={index}
          />
        ))}

        {/* Load more indicator */}
        {hasNext && !onLoading && (
          <LoadMoreTrigger onLoadMore={onLoadMore} />
        )}

        {/* Loading more indicator */}
        {onLoading && comments?.length > 0 && <LoadingIndicator />}

        {/* Initial topic message */}
        {!hasNext && topic?.message?.trim() !== "" && (
          <MessageBubble
            key={`topic-${topic?.id}`}
            message={topic.message}
            timestamp={topic.createdAt}
            isMe={topic.userId === userProfile?.userId}
            isMyAgency={
              topic.companyId === userProfile?.companyId &&
              topic.userId !== userProfile?.userId
            }
            index={comments?.length || 0}
          />
        )}

        {/* Empty state */}
        {!onLoading && (!comments || comments.length === 0) && !topic?.message?.trim() && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div
              className={cn(
                "w-12 h-12 rounded-xl mb-3",
                "bg-slate-100 dark:bg-slate-800",
                "flex items-center justify-center"
              )}
            >
              <svg
                className="w-6 h-6 text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="font-sans text-sm text-muted-foreground">
              No messages yet
            </p>
            <p className="font-mono text-xs text-slate-400 dark:text-slate-500 mt-1">
              Start the conversation below
            </p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default MessageThread;
